import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { BRAND } from "./data";
import { MaskLines } from "./Reveal";
import { useContent } from "./ContentContext";
import { useTx, txtStyle } from "./siteText";
import { OpenBanner } from "./OpenBanner";

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { settings, resolveImg } = useContent();
  const tx = useTx();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlayFade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-cantina-navydeep" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        {settings.images.heroVideo ? (
          <video
            className="w-full h-full object-cover"
            src={resolveImg(settings.images.heroVideo)}
            poster={resolveImg(settings.images.hero)}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid="hero-video"
          />
        ) : (
          <motion.img
            src={resolveImg(settings.images.hero)}
            alt="Blue Tequila Mexican feast"
            className="w-full h-full object-cover"
            initial={{ scale: 1.12 }}
            animate={{ scale: 1.22 }}
            transition={{ duration: 22, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
          />
        )}
      </motion.div>

      {/* overlays for legibility */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(7,24,71,0.92) 0%, rgba(7,24,71,0.72) 34%, rgba(7,24,71,0.4) 58%, rgba(7,24,71,0.12) 80%, rgba(7,24,71,0.04) 100%)" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,24,71,0.45) 0%, transparent 22%, transparent 52%, rgba(7,24,71,0.9) 100%)" }} />
      {/* cinematic vignette for guaranteed contrast on bright frames */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(130% 100% at 28% 50%, transparent 38%, rgba(4,14,45,0.6) 100%)" }} />

      <motion.div style={{ opacity: overlayFade }} className="relative z-10 min-h-[100svh] max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-center pt-[128px] pb-24 md:pb-16">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="mb-6 md:mb-8">
          <OpenBanner />
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.4 }} className="flex items-center gap-3 mb-5">
          <span className="w-10 h-px bg-cantina-marigold" />
          <span className="font-mono tracking-[0.3em] uppercase text-cantina-marigold" style={txtStyle(tx("hero.eyebrow"))}>{tx("hero.eyebrow").text}</span>
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.5 }}
          className="font-script text-cantina-marigold/95 mb-1 leading-none" style={txtStyle(tx("hero.script"))}>
          {tx("hero.script").text}
        </motion.p>

        <h1 className="font-display text-cantina-cream tracking-tight leading-[0.9] max-w-[16ch]" style={txtStyle(tx("hero.headline"))}>
          <MaskLines lines={tx("hero.headline").text.split("\n")} delay={0.55} stagger={0.14} />
        </h1>

        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-6 max-w-lg font-sans leading-relaxed text-cantina-cream/80" style={txtStyle(tx("hero.paragraph"))}>
          {tx("hero.paragraph").text}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <a href={BRAND.orderUrl} target="_blank" rel="noopener noreferrer" className="btn-primary bg-cantina-marigold text-cantina-navy font-sans tracking-wide px-8 py-4 rounded-full" style={{ boxShadow: "0 14px 34px rgba(233,196,106,0.32)", ...txtStyle(tx("hero.orderLabel")) }} data-testid="hero-order-button">
            {tx("hero.orderLabel").text}
          </a>
          <button onClick={() => navigate("/menu")} className="btn-outline font-sans tracking-wide text-cantina-cream border-2 border-cantina-cream/40 hover:border-cantina-cream px-8 py-[14px] rounded-full" style={{ backgroundColor: "transparent", ...txtStyle(tx("hero.menuLabel")) }} data-testid="hero-menu-button">
            {tx("hero.menuLabel").text}
          </button>
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity: overlayFade }} className="absolute bottom-7 right-6 md:right-10 z-10 flex items-center gap-3 text-cantina-cream/70">
        <span className="font-mono tracking-[0.3em] uppercase" style={txtStyle(tx("hero.scroll"))}>{tx("hero.scroll").text}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-cantina-marigold" />
        </motion.span>
      </motion.div>
    </section>
  );
};
